import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useLazyQuery, useReactiveVar } from "@apollo/client";
import { DateTime } from "luxon";

import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import { SalesDBType, DashboardData } from "../../../models/sales_model";
import { GETSALES, GET_SALES_DASHBOARD } from "./queries";
import SalesResume from "./resume";
import Charts from "./charts";
import { activeBranchVar } from "../../../middleware/cache";

import SalesNav from "./salesNav";
import SalesOrders from "./orders";
import SalesProducts from "./products";
import SalesByPerson from "./byPerson";

export interface SalesFetchedData {
  getSales: SalesDBType;
}

interface DashboardFetchedData {
  getSalesDashboard: DashboardData;
}

type SalesDates = {
  start: string;
  startDate: Date;
  end: string;
  endDate: Date;
};

const todayDateTime = DateTime.local();

const SalesPanel: React.FC = () => {
  const { section } = useParams<{ section: string }>();
  const activeBranch = useReactiveVar(activeBranchVar);

  const [dates, setDates] = useState<SalesDates>({
    start: todayDateTime.toISODate(),
    end: todayDateTime.toISODate(),
    startDate: new Date(),
    endDate: new Date(),
  });

  // Dashboard query for Resumen tab (aggregated metrics)
  const [getDashboard, { loading: dashboardLoading, data: dashboardData }] =
    useLazyQuery<DashboardFetchedData>(GET_SALES_DASHBOARD, {
      fetchPolicy: "no-cache",
    });

  // Raw orders query for Orders/Products/By-Person tabs (lazy)
  const [getSales, { loading: salesLoading, data: salesData }] =
    useLazyQuery<SalesFetchedData>(GETSALES, {
      fetchPolicy: "no-cache",
    });

  const queryVars = {
    branch_id: +activeBranch,
    startDate: dates.start,
    endDate: dates.end,
  };

  // Always fetch dashboard data
  useEffect(() => {
    getDashboard({ variables: queryVars });
    //eslint-disable-next-line
  }, [dates, activeBranch]);

  // Fetch raw orders only when needed for detail tabs
  useEffect(() => {
    if (section === "orders" || section === "products" || section === "by-person") {
      getSales({ variables: queryVars });
    }
    //eslint-disable-next-line
  }, [section, dates, activeBranch]);

  const onChangeDates = (changed_dates: [Date, Date]) => {
    const [start, end] = changed_dates;
    const startDate = DateTime.fromJSDate(start);
    const endDate = DateTime.fromJSDate(end);
    setDates({
      start: startDate.toISODate(),
      end: endDate.toISODate(),
      startDate: start,
      endDate: end,
    });
  };

  const loading = dashboardLoading || salesLoading;
  const dashboard = dashboardData?.getSalesDashboard;
  const isMultiDay =
    dates.start !== dates.end && !!dates.end;

  function renderView() {
    switch (section) {
      case "orders":
        if (!salesData || salesData.getSales.orders.length < 1) return <NoSales />;
        return <SalesOrders sales_data={salesData.getSales} />;
      case "products":
        if (!salesData || salesData.getSales.orders.length < 1) return <NoSales />;
        return <SalesProducts orders={salesData.getSales.orders} />;
      case "by-person":
        if (!salesData || salesData.getSales.orders.length < 1) return <NoSales />;
        return (
          <SalesByPerson
            sales_data={salesData.getSales}
            dashboardPersonData={dashboard?.salesByPerson}
          />
        );
      default:
        if (!dashboard || dashboard.totalOrders === 0) return <NoSales />;
        return <Charts dashboard={dashboard} isMultiDay={isMultiDay} />;
    }
  }

  return (
    <main className="sales_page">
      <div className="container">
        {loading ? <LoaderDots fullSize={true} /> : null}

        <SalesResume
          dashboard={dashboard || null}
          startDate={dates.startDate}
          endDate={dates.endDate}
          onChange={onChangeDates}
        />

        <SalesNav />

        {renderView()}
      </div>
    </main>
  );
};

const NoSales: React.FC = () => (
  <div
    className="justify-content-center flex-column align-content-center text-center d-flex"
    id="no_order"
  >
    <h1 className="full_page_notice" style={{ marginTop: "10%" }}>
      <i className="fa-3 text-secondary fas fa-search-dollar"></i>
    </h1>
    <h3 className="text-muted text-center">
      {lang("No hay ventas", "No sales")}
    </h3>
  </div>
);

export default SalesPanel;
